import React, { Dispatch, SetStateAction } from "react";

import { SocialProviderIcon2 } from "src/app/pages/CreatorDiscoveryTool/legacy/app/components/SocialProviderIcon2/SocialProviderIcon2";
import publicationPlaceholder from "src/images/eye-placeholder.svg";
import moment from "moment";
import OuterLink from "src/app/components/OuterLink/OuterLink";
import { Img } from "src/app/components/Img/Img";
import CustomCheckbox from "src/app/components/CustomCheckbox/CustomCheckbox";
import classNames from "classnames";
import Pictogram, {
  PictogramVariants,
} from "src/app/components/Pictogram/Pictogram";
import { formatNumber, toFixedIfNecessary } from "src/utils/methods";
import {
  GalleryCreatorData,
  SocialProvider,
  convertTikTokToWorkspaceVersion,
} from "src/types";
import TooltipPortal from "src/app/components/TooltipPortal/TooltipPortal";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";

import creatorPlaceholder from "src/images/creator-placeholder.svg";

import "./GalleryItem.scss";

export interface GalleryListElement {
  description: string;
  engagementRate: number | null;
  imageUrl: string;
  publishedDate: string;
  url: string;
  thumbUrl: string;
  socialProvider: string;
  mediaType: string;
  keywords?: string[];
  likes: number | null;
  comments: number | null;
  views: number | null;
  creatorData?: GalleryCreatorData;
  campaignData?: {
    uuid: string;
    name: string | null;
    coverUrl: string | null;
  } | null;
  isPaidPost?: boolean;
  isPaidPostSource?: "native" | "heuristic";
}

interface Props {
  data: GalleryListElement;
  index: number;
  setSlideIndex: Dispatch<SetStateAction<number | null>>;
  displayCheckbox: boolean;
  selectedPostIds: string[];
  setSelectedPostIds: Dispatch<SetStateAction<string[]>>;
}

export function GalleryItem(props: Props) {
  const {
    data,
    index,
    setSlideIndex,
    displayCheckbox,
    selectedPostIds,
    setSelectedPostIds,
  } = props;

  const checked = selectedPostIds.includes(data.url);

  const toggleChecked = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (selectedPostIds.includes(data.url)) {
      setSelectedPostIds(selectedPostIds.filter((id) => id !== data.url));
    } else {
      setSelectedPostIds([...selectedPostIds, data.url]);
    }
  };

  const stats = {
    er: { value: data.engagementRate, type: "percent" },
    likes: { value: data.likes, type: "number" },
    views: { value: data.views, type: "number" },
    comments: { value: data.comments, type: "number" },
  };

  const statsCount = Object.values(stats).filter((item) => item.value).length;

  const renderStatValue = (value: number, type: string) => {
    if (type === "percent") {
      return `${toFixedIfNecessary((value ?? 0) * 100, 2)}%`;
    }
    if (type === "number") {
      return formatNumber(value);
    }
    return 0;
  };

  return (
    <div className="gallery-item-wrapper" data-qa={data.url}>
      <div
        className={classNames("gallery-item", {
          "gallery-item--checked": checked,
        })}
      >
        <div className="gallery-item__provider">
          <div className="gallery-item__provider-header">
            {data.creatorData ? (
              <>
                <Img
                  src={data?.creatorData?.profilePictureUrl}
                  fallbackImageSrc={creatorPlaceholder}
                  referrerPolicy="no-referrer"
                />
                <span className="gallery-item__provider-header-text">
                  {data?.creatorData?.username}
                </span>
              </>
            ) : (
              <span className="gallery-item__provider-header-text" />
            )}
          </div>
          <span className="gallery-item__provider-icon">
            {data.url?.length > 0 ? (
              <OuterLink
                className="gallery-item__provider-link"
                href={data.url}
              >
                <SocialProviderIcon2 provider={data.socialProvider} />
              </OuterLink>
            ) : (
              <SocialProviderIcon2 provider={data.socialProvider} />
            )}
          </span>
        </div>

        <div className="gallery-item__thumbnail">
          {displayCheckbox && data.url && data.url.length > 0 && (
            <CustomCheckbox
              id={`gallery-checkbox-${data.url}`}
              name={`gallery-checkbox-${data.url}`}
              className={classNames("gallery-item__checkbox", {
                "gallery-item__checkbox--checked": checked,
              })}
              checked={checked}
              onChange={toggleChecked}
            />
          )}
          <div
            className="gallery-item__thumbnail-overlay"
            onClick={() => setSlideIndex(index)}
          />
          <Img
            src={data.thumbUrl}
            fallbackImageSrc={publicationPlaceholder}
            referrerPolicy="no-referrer"
            alt="thumbnail"
            loading="lazy"
            // TikTok-only HEIC recovery, see task:
            // https://app.asana.com/1/242052987772680/project/807220246535156/task/1217546080711977?focus=true
            allowHeicRecovery={
              convertTikTokToWorkspaceVersion(data.socialProvider) ===
              SocialProvider.TikTok
            }
            heicRecoveryMaxDimension={221}
          />
          {data.isPaidPost && (
            <TooltipPortal
              className="gallery-item__paid-post-badge-tooltip"
              content={
                data.isPaidPostSource === "native" ? (
                  <IDHFormattedMessage
                    id="ws_is_paid_post_native_tooltip"
                    defaultMessage="Paid partnership - marked by the creator."
                  />
                ) : (
                  <IDHFormattedMessage
                    id="ws_is_paid_post_tooltip"
                    defaultMessage="Possibly sponsored - detected based on the post's content."
                  />
                )
              }
            >
              <span className="gallery-item__paid-post-badge">
                <svg
                  className="gallery-item__paid-post-badge-icon"
                  viewBox="0 0 16 16"
                  aria-hidden="true"
                >
                  {data.isPaidPostSource === "native" ? (
                    <circle cx="8" cy="8" r="7" fill="currentColor" />
                  ) : (
                    <circle
                      cx="8"
                      cy="8"
                      r="7"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                  )}
                  <text
                    x="8"
                    y="8.5"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill={
                      data.isPaidPostSource === "native"
                        ? "#000"
                        : "currentColor"
                    }
                    fontSize="9"
                    fontWeight="700"
                  >
                    $
                  </text>
                </svg>
              </span>
            </TooltipPortal>
          )}
        </div>
        <div className="gallery-item__data">
          <span className="gallery-item__data-item">
            {data?.publishedDate &&
              moment(data.publishedDate).format("DD.MM.YYYY").toString()}
          </span>

          {statsCount % 2 === 0 && <span className="gallery-item__data-item" />}

          {Object.entries(stats).map(([key, item]) => {
            if (!item.value) return null;

            return (
              <span className="gallery-item__data-item">
                <Pictogram
                  identifier={`Social/${key}`}
                  variant={PictogramVariants.CoolGrey}
                />
                {renderStatValue(item.value, item.type)}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}
